import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { api, ApiError } from '@/lib/api';
import { userKeys } from '@/lib/queryKeys';
import { Button, Input } from '@rhpc/ui';
import { PageHeader, PageShell } from '@/components/PageShell';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Role = 'STAFF' | 'ADMIN' | 'FINANCE' | 'FINANCE_LEAD' | 'HR' | 'IT_ADMIN';

interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: Role;
  department: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface UsersResponse {
  users: AdminUser[];
  meta: { pagination: { page: number; limit: number; total: number; totalPages: number } };
}

interface CreateUserFormData {
  name: string;
  email: string;
  role: Role;
  department: string;
}

// ---------------------------------------------------------------------------
// Create User Dialog
// ---------------------------------------------------------------------------

interface CreateUserDialogProps {
  onClose: () => void;
}

function CreateUserDialog({ onClose }: CreateUserDialogProps) {
  const queryClient = useQueryClient();
  const [serverError, setServerError] = useState<string | null>(null);

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<CreateUserFormData>({
    defaultValues: { role: 'STAFF' },
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateUserFormData) =>
      api.post<{ user: AdminUser; message: string }>('/admin/users', {
        ...data,
        department: data.department || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      onClose();
    },
    onError: (err) => {
      setServerError(err instanceof ApiError ? err.message : 'Failed to create user');
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-xl bg-surface border border-border shadow-2xl">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-lg font-semibold text-text">Create New User</h2>
          <button onClick={onClose} className="text-text-2 hover:text-text transition-colors text-xl leading-none">×</button>
        </div>

        <form
          onSubmit={handleSubmit((d) => createMutation.mutate(d))}
          className="p-6 space-y-4"
          noValidate
        >
          {serverError && (
            <div className="rounded-md bg-danger-soft border border-danger/20 p-3" role="alert">
              <p className="text-sm text-danger">{serverError}</p>
            </div>
          )}

          <Input
            id="create-name"
            label="Full Name"
            required
            error={errors.name?.message}
            {...register('name', { required: 'Name is required', minLength: { value: 2, message: 'Min 2 characters' } })}
          />
          <Input
            id="create-email"
            label="Email Address"
            type="email"
            required
            error={errors.email?.message}
            {...register('email', { required: 'Email is required', pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Valid email required' } })}
          />

          <div className="space-y-1.5">
            <label htmlFor="create-role" className="block text-sm font-medium text-text">
              Role <span aria-hidden="true" className="text-danger ml-0.5">*</span>
            </label>
            <select
              id="create-role"
              className="flex h-10 w-full rounded-md border border-input bg-surface px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              {...register('role', { required: true })}
            >
              <option value="STAFF">Staff</option>
              <option value="ADMIN">Admin</option>
              <option value="FINANCE">Finance</option>
              <option value="FINANCE_LEAD">Finance Lead</option>
              <option value="HR">HR</option>
              <option value="IT_ADMIN">IT Admin</option>
            </select>
          </div>

          <Input
            id="create-dept"
            label="Department"
            placeholder="e.g. Engineering"
            error={errors.department?.message}
            {...register('department')}
          />

          <p className="text-xs text-text-2">
            A temporary password will be generated and sent to the user's email.
          </p>

          <div className="flex gap-3 pt-2 justify-end">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" loading={isSubmitting || createMutation.isPending}>
              Create User
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Confirm Dialog
// ---------------------------------------------------------------------------

interface ConfirmDialogProps {
  title: string;
  message: string;
  confirmLabel: string;
  variant?: 'default' | 'destructive';
  onConfirm: () => void;
  onCancel: () => void;
}

function RoleSelect({
  user,
  onChange,
  disabled,
}: {
  user: AdminUser;
  onChange: (role: Role) => void;
  disabled?: boolean;
}) {
  return (
    <select
      value={user.role}
      onChange={(e) => onChange(e.target.value as Role)}
      disabled={disabled}
      className="text-xs rounded border border-input bg-surface px-2 py-1.5 cursor-pointer focus:ring-1 focus:outline-primary w-full max-w-[160px]"
      aria-label={`Change role for ${user.name}`}
    >
      <option value="STAFF">STAFF</option>
      <option value="ADMIN">ADMIN</option>
      <option value="FINANCE">FINANCE</option>
      <option value="FINANCE_LEAD">FINANCE LEAD</option>
      <option value="HR">HR</option>
      <option value="IT_ADMIN">IT ADMIN</option>
    </select>
  );
}

function StatusPill({ isActive }: { isActive: boolean }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
      isActive ? 'bg-ok-soft text-ok' : 'bg-surface-2 text-text-3'
    }`}>
      {isActive ? 'Active' : 'Inactive'}
    </span>
  );
}

function UserActionButton({
  user,
  onToggle,
}: {
  user: AdminUser;
  onToggle: (action: 'activate' | 'deactivate') => void;
}) {
  if (user.isActive) {
    return (
      <Button
        size="sm"
        variant="outline"
        onClick={() => onToggle('deactivate')}
      >
        Deactivate
      </Button>
    );
  }
  return (
    <Button size="sm" onClick={() => onToggle('activate')}>
      Activate
    </Button>
  );
}

function ConfirmDialog({ title, message, confirmLabel, variant = 'default', onConfirm, onCancel }: ConfirmDialogProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-sm rounded-xl bg-surface border border-border shadow-2xl p-6 space-y-4">
        <h2 className="text-lg font-semibold text-text">{title}</h2>
        <p className="text-sm text-text-2">{message}</p>
        <div className="flex gap-3 justify-end">
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button variant={variant} onClick={onConfirm}>{confirmLabel}</Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export function UserManagementPage() {
  const queryClient = useQueryClient();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{
    userId: string;
    userName: string;
    action: 'activate' | 'deactivate' | 'resend';
  } | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  // Fetch users
  const { data, isLoading, error } = useQuery({
    queryKey: userKeys.list({ page }),
    queryFn: () => api.get<UsersResponse>(`/admin/users?page=${page}&limit=50`),
  });

  // Role/field update mutation
  const updateUserMutation = useMutation({
    mutationFn: ({ userId, updates }: { userId: string; updates: Partial<{ role: Role; name: string; department: string }> }) =>
      api.patch<{ user: AdminUser; message: string }>(`/admin/users/${userId}`, updates),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      showSuccess(result.message ?? 'User updated');
    },
  });

  // Status toggle mutation
  const toggleStatusMutation = useMutation({
    mutationFn: ({ userId, isActive }: { userId: string; isActive: boolean }) =>
      api.patch<{ user: AdminUser; message: string }>(`/admin/users/${userId}/status`, { isActive }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      showSuccess(result.message ?? 'Status updated');
      setConfirmAction(null);
    },
  });

  const resendWelcomeMutation = useMutation({
    mutationFn: (userId: string) =>
      api.post<{ message: string; emailStatus: string }>(`/admin/users/${userId}/resend-welcome`, {}),
    onSuccess: (result) => {
      showSuccess(result.message ?? 'Welcome email queued');
      setConfirmAction(null);
    },
  });

  function showSuccess(msg: string) {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 4000);
  }

  const users = data?.users ?? [];
  const pagination = data?.meta.pagination;

  return (
    <PageShell>
      {/* Dialogs */}
      {showCreateDialog && <CreateUserDialog onClose={() => setShowCreateDialog(false)} />}
      {confirmAction && (
        <ConfirmDialog
          title={
            confirmAction.action === 'deactivate'
              ? 'Deactivate User'
              : confirmAction.action === 'activate'
                ? 'Activate User'
                : 'Resend welcome email'
          }
          message={
            confirmAction.action === 'deactivate'
              ? `Deactivate ${confirmAction.userName}? Their active sessions will be invalidated.`
              : confirmAction.action === 'activate'
                ? `Reactivate ${confirmAction.userName}? They will be able to log in again.`
                : `Send ${confirmAction.userName} a new temporary password? They must change it before using the system.`
          }
          confirmLabel={
            confirmAction.action === 'deactivate'
              ? 'Deactivate'
              : confirmAction.action === 'activate'
                ? 'Activate'
                : 'Resend'
          }
          variant={confirmAction.action === 'deactivate' ? 'destructive' : 'default'}
          onConfirm={() => {
            if (confirmAction.action === 'resend') {
              resendWelcomeMutation.mutate(confirmAction.userId);
              return;
            }
            toggleStatusMutation.mutate({
              userId: confirmAction.userId,
              isActive: confirmAction.action === 'activate',
            });
          }}
          onCancel={() => setConfirmAction(null)}
        />
      )}

      <PageHeader
        title="User Management"
        description={
          pagination
            ? `${pagination.total} user${pagination.total !== 1 ? 's' : ''} total`
            : 'Manage system access'
        }
        actions={<Button onClick={() => setShowCreateDialog(true)}>+ Create User</Button>}
      />

      {/* Success toast */}
      {successMsg && (
        <div className="mb-6 rounded-md bg-ok-soft border border-ok/20 p-3 flex items-center gap-2" role="status">
          <span className="text-ok">✓</span>
          <p className="text-sm text-ok">{successMsg}</p>
        </div>
      )}

      {/* Table */}
      <div className="rounded-lg border border-border bg-surface shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-8 space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-12 bg-surface-2 rounded animate-pulse" />
            ))}
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <p className="text-danger text-sm">Failed to load users. Please refresh.</p>
          </div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-text-2 text-sm">No users found.</p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-border lg:hidden">
              {users.map((user) => (
                <div key={user.id} className="space-y-3 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-2">
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                        {user.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="truncate font-medium text-text">{user.name}</p>
                        <p className="truncate font-mono text-xs text-text-2">{user.email}</p>
                      </div>
                    </div>
                    <StatusPill isActive={user.isActive} />
                  </div>
                  <p className="text-xs text-text-3">{user.department ?? 'No department'}</p>
                  <div className="flex items-center justify-between gap-3">
                    <RoleSelect
                      user={user}
                      disabled={updateUserMutation.isPending}
                      onChange={(role) => updateUserMutation.mutate({ userId: user.id, updates: { role } })}
                    />
                    <UserActionButton
                      user={user}
                      onToggle={(action) =>
                        setConfirmAction({ userId: user.id, userName: user.name, action })
                      }
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setConfirmAction({ userId: user.id, userName: user.name, action: 'resend' })
                      }
                    >
                      Resend welcome
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <div className="hidden overflow-x-auto lg:block">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-surface-2">
                    <th className="text-left px-4 py-3 text-[11px] font-bold uppercase tracking-[.12em] text-text-3">Name</th>
                    <th className="text-left px-4 py-3 text-[11px] font-bold uppercase tracking-[.12em] text-text-3">Email</th>
                    <th className="text-left px-4 py-3 text-[11px] font-bold uppercase tracking-[.12em] text-text-3">Role</th>
                    <th className="hidden text-left px-4 py-3 text-[11px] font-bold uppercase tracking-[.12em] text-text-3 xl:table-cell">Department</th>
                    <th className="text-left px-4 py-3 text-[11px] font-bold uppercase tracking-[.12em] text-text-3">Status</th>
                    <th className="sticky right-0 bg-surface-2 text-right px-4 py-3 text-[11px] font-bold uppercase tracking-[.12em] text-text-3 shadow-[-8px_0_12px_-8px_rgba(11,18,48,0.18)]">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {users.map((user) => (
                    <tr key={user.id} className="group hover:bg-surface-2 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary shrink-0">
                            {user.name.charAt(0).toUpperCase()}
                          </div>
                          <span className="font-medium text-text">{user.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-text-2">{user.email}</td>
                      <td className="px-4 py-3">
                        <RoleSelect
                          user={user}
                          disabled={updateUserMutation.isPending}
                          onChange={(role) => updateUserMutation.mutate({ userId: user.id, updates: { role } })}
                        />
                      </td>
                      <td className="hidden px-4 py-3 text-text-2 xl:table-cell">{user.department ?? '—'}</td>
                      <td className="px-4 py-3">
                        <StatusPill isActive={user.isActive} />
                      </td>
                      <td className="sticky right-0 bg-surface px-4 py-3 text-right shadow-[-8px_0_12px_-8px_rgba(11,18,48,0.18)] group-hover:bg-surface-2">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              setConfirmAction({ userId: user.id, userName: user.name, action: 'resend' })
                            }
                          >
                            Resend welcome
                          </Button>
                          <UserActionButton
                            user={user}
                            onToggle={(action) =>
                              setConfirmAction({ userId: user.id, userName: user.name, action })
                            }
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="border-t border-border px-4 py-3 flex items-center justify-between">
            <p className="text-sm text-text-2">
              Page {pagination.page} of {pagination.totalPages}
            </p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                Previous
              </Button>
              <Button size="sm" variant="outline" disabled={page === pagination.totalPages} onClick={() => setPage(p => p + 1)}>
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </PageShell>
  );
}
